import React, { createContext, useContext, useEffect, useState } from "react";
import { 
  onAuthStateChanged, 
  signOut,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword
} from "firebase/auth";
import { ref, onValue } from "firebase/database";
import { auth, db } from "../firebase/config";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        setLoading(true); // Ensure loading is true while fetching clinical metadata
        const userRef = ref(db, `users/${user.uid}`);
        const caregiverRef = ref(db, `caregivers/${user.uid}`);
        
        // Identity Discovery Trace
        onValue(userRef, (userSnapshot) => {
          const userData = userSnapshot.val();
          if (userData) {
            setUserData(userData);
            setLoading(false);
          } else {
            // Check decentralized Caregiver node
            onValue(caregiverRef, (cgSnapshot) => {
              const cgData = cgSnapshot.val();
              if (cgData) {
                setUserData(cgData);
              } else {
                // Default clinical profile fallback
                setUserData({ 
                  role: 'patient', 
                  name: user.email ? user.email.split('@')[0] : 'User', 
                  status: 'active' 
                });
              }
              setLoading(false);
            }, { onlyOnce: true });
          }
        });
      } else {
        setUserData(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const logout = () => signOut(auth);

  const reauthenticate = (currentPassword) => {
    if (!currentUser) return Promise.reject("No user logged in");
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    return reauthenticateWithCredential(currentUser, credential);
  };

  const updateSessionPassword = (newPassword) => {
    if (!currentUser) return Promise.reject("No user logged in");
    return updatePassword(currentUser, newPassword);
  };

  const refreshUser = async () => {
    if (!currentUser) return;
    await currentUser.reload();
    setCurrentUser({ ...auth.currentUser });
  };

  const value = {
    currentUser,
    userData,
    loading,
    logout,
    reauthenticate,
    updateSessionPassword,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
